import type { InteractionMetrics } from '../domain/life-state'

export interface InteractionSample {
  x: number
  y: number
  at: number
  active: boolean
}

type InteractionKind = 'gentle' | 'burst' | 'presence'

const MAX_WINDOW_MS = 60_000
const MAX_GENTLE_INTERVAL_MS = 5_000
const MAX_ACTIVE_BURSTS = 120
const MAX_INTERACTION_KINDS = 4
const GENTLE_SPEED_THRESHOLD = 0.0002
const BURST_SPEED_THRESHOLD = 0.002

export class InteractionEngine {
  private firstActiveAt: number | null = null
  private lastObservedAt: number | null = null
  private previousActive: InteractionSample | null = null
  private gentleMs = 0
  private activeBursts = 0
  private readonly kinds = new Set<InteractionKind>()

  observe(sample: InteractionSample): void {
    if (!isValidSample(sample) || !this.isMonotonic(sample.at)) {
      return
    }

    this.lastObservedAt = sample.at

    if (!sample.active) {
      this.previousActive = null
      return
    }

    const normalized = normalizeSample(sample)
    this.firstActiveAt ??= normalized.at
    this.kinds.add('presence')

    if (this.previousActive) {
      const elapsed = normalized.at - this.previousActive.at
      const distance = Math.hypot(
        normalized.x - this.previousActive.x,
        normalized.y - this.previousActive.y,
      )
      const speed = distance / elapsed

      if (speed < GENTLE_SPEED_THRESHOLD) {
        this.gentleMs += Math.min(elapsed, MAX_GENTLE_INTERVAL_MS)
        this.kinds.add('gentle')
      } else if (speed > BURST_SPEED_THRESHOLD) {
        this.activeBursts += 1
        this.kinds.add('burst')
      }
    }

    this.previousActive = normalized
  }

  flush(now: number): InteractionMetrics {
    const safeNow = Number.isFinite(now) ? now : (this.lastObservedAt ?? 0)
    const presenceMs = this.firstActiveAt === null
      ? 0
      : Math.min(MAX_WINDOW_MS, Math.max(0, safeNow - this.firstActiveAt))

    const metrics: InteractionMetrics = {
      gentleMs: Math.min(MAX_WINDOW_MS, Math.max(0, Math.round(this.gentleMs))),
      activeBursts: Math.min(MAX_ACTIVE_BURSTS, Math.max(0, this.activeBursts)),
      interactionKinds: Math.min(MAX_INTERACTION_KINDS, this.kinds.size),
      presenceMs: Math.round(presenceMs),
    }

    this.reset()
    return metrics
  }

  private isMonotonic(at: number): boolean {
    return this.lastObservedAt === null || at > this.lastObservedAt
  }

  private reset(): void {
    this.firstActiveAt = null
    this.lastObservedAt = null
    this.previousActive = null
    this.gentleMs = 0
    this.activeBursts = 0
    this.kinds.clear()
  }
}

function isValidSample(sample: InteractionSample): boolean {
  return Number.isFinite(sample.x)
    && Number.isFinite(sample.y)
    && Number.isFinite(sample.at)
}

function normalizeSample(sample: InteractionSample): InteractionSample {
  return {
    x: Math.min(1, Math.max(0, sample.x)),
    y: Math.min(1, Math.max(0, sample.y)),
    at: sample.at,
    active: true,
  }
}
